'use client';

import { useState, useCallback, useEffect } from 'react';
import styles from './Calendar.module.css';

export interface CalendarProps {
  /** The currently selected date */
  value?: Date;
  /** Callback when a date is selected */
  onChange?: (date: Date) => void;
  /** Minimum selectable date */
  minDate?: Date;
  /** Maximum selectable date */
  maxDate?: Date;
  /** Whether the calendar is disabled */
  disabled?: boolean;
  /** Custom className for the calendar wrapper */
  className?: string;
  /** Label for accessibility */
  label?: string;
  /** Whether to show week numbers */
  showWeekNumbers?: boolean;
  /** Custom month/year format function */
  formatMonthYear?: (date: Date) => string;
  /** Custom day format function */
  formatDay?: (date: Date) => string;
  /** Custom weekday format function */
  formatWeekday?: (date: Date) => string;
}

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];

export function Calendar({
  value,
  onChange,
  minDate,
  maxDate,
  disabled = false,
  className = '',
  label,
  showWeekNumbers = false,
  formatMonthYear = (date) => `${MONTHS[date.getMonth()]} ${date.getFullYear()}`,
  formatDay = (date) => date.getDate().toString(),
  formatWeekday = (date) => WEEKDAYS[date.getDay()],
}: CalendarProps) {
  const [currentMonth, setCurrentMonth] = useState(() => {
    // If a date is already selected, show that month/year
    if (value) {
      return new Date(value.getFullYear(), value.getMonth(), 1);
    }
    const today = new Date();
    return new Date(today.getFullYear(), today.getMonth(), 1);
  });

  // Get calendar days for the current month
  const getCalendarDays = useCallback((date: Date) => {
    const year = date.getFullYear();
    const month = date.getMonth();

    // First day of the month
    const firstDay = new Date(year, month, 1);
    // Last day of the month
    const lastDay = new Date(year, month + 1, 0);

    // Start from the first Sunday of the week containing the first day
    const startDate = new Date(firstDay);
    startDate.setDate(firstDay.getDate() - firstDay.getDay());

    // End on the last Saturday of the week containing the last day
    const endDate = new Date(lastDay);
    endDate.setDate(lastDay.getDate() + (6 - lastDay.getDay()));

    const days = [];
    let currentDate = new Date(startDate);

    while (currentDate <= endDate) {
      days.push(new Date(currentDate));
      currentDate.setDate(currentDate.getDate() + 1);
    }

    return days;
  }, []);

  const calendarDays = getCalendarDays(currentMonth);

  // Check if a date is selected
  const isSelected = useCallback((date: Date) => {
    if (!value) return false;
    return (
      date.getDate() === value.getDate() &&
      date.getMonth() === value.getMonth() &&
      date.getFullYear() === value.getFullYear()
    );
  }, [value]);

  // Check if a date is today
  const isToday = useCallback((date: Date) => {
    const today = new Date();
    return (
      date.getDate() === today.getDate() &&
      date.getMonth() === today.getMonth() &&
      date.getFullYear() === today.getFullYear()
    );
  }, []);

  // Check if a date is in the current month
  const isCurrentMonth = useCallback((date: Date) => {
    return date.getMonth() === currentMonth.getMonth();
  }, [currentMonth]);

  // Check if a date is disabled
  const isDisabled = useCallback((date: Date) => {
    if (disabled) return true;
    if (minDate && date < minDate) return true;
    if (maxDate && date > maxDate) return true;
    return false;
  }, [disabled, minDate, maxDate]);

  // Handle date selection
  const handleDateSelect = useCallback((date: Date) => {
    if (isDisabled(date) || !onChange) return;
    onChange(date);
  }, [isDisabled, onChange]);

  // Generate year options (current year ± 50 years for birth dates)
  const generateYearOptions = useCallback(() => {
    const currentYear = new Date().getFullYear();
    const years = [];
    for (let year = currentYear - 50; year <= currentYear; year++) {
      years.push(year);
    }
    return years.reverse(); // Most recent years first
  }, []);

  const yearOptions = generateYearOptions();
  const [showDropdown, setShowDropdown] = useState(false);
  const [selectedYear, setSelectedYear] = useState<number | null>(null);

  // Update current month when value changes
  useEffect(() => {
    if (value) {
      setCurrentMonth(new Date(value.getFullYear(), value.getMonth(), 1));
    }
  }, [value]);

  // Navigate to previous month
  const goToPreviousMonth = useCallback(() => {
    setCurrentMonth(prev => new Date(prev.getFullYear(), prev.getMonth() - 1, 1));
  }, []);

  // Navigate to next month
  const goToNextMonth = useCallback(() => {
    setCurrentMonth(prev => new Date(prev.getFullYear(), prev.getMonth() + 1, 1));
  }, []);

  // Navigate to specific month/year
  const goToMonth = useCallback((month: number, year?: number) => {
    setCurrentMonth(new Date(year || new Date().getFullYear(), month, 1));
    setShowDropdown(false);
    setSelectedYear(null);
  }, []);

  // Handle year selection from dropdown
  const handleYearSelect = useCallback((year: number) => {
    setSelectedYear(year);
    // Keep dropdown open to show month selection
  }, []);

  // Handle month selection from grid
  const handleMonthSelect = useCallback((month: number) => {
    if (selectedYear) {
      setCurrentMonth(new Date(selectedYear, month, 1));
      setShowDropdown(false);
      setSelectedYear(null);
    }
  }, [selectedYear]);

  // Handle keyboard navigation
  const handleKeyDown = useCallback((e: React.KeyboardEvent, date: Date) => {
    if (isDisabled(date)) return;

    switch (e.key) {
      case 'Enter':
      case ' ':
        e.preventDefault();
        handleDateSelect(date);
        break;
      case 'ArrowLeft':
        e.preventDefault();
        // Focus previous day - would need to implement focus management
        break;
      case 'ArrowRight':
        e.preventDefault();
        // Focus next day - would need to implement focus management
        break;
      case 'ArrowUp':
        e.preventDefault();
        // Focus day above - would need to implement focus management
        break;
      case 'ArrowDown':
        e.preventDefault();
        // Focus day below - would need to implement focus management
        break;
    }
  }, [isDisabled, handleDateSelect]);

  return (
    <div className={`${styles.calendar} ${className}`}>
      {label && (
        <label className={styles.label}>
          {label}
        </label>
      )}

      {/* Calendar Header with enhanced year navigation */}
      <div className={styles.header}>
        <button
          type="button"
          className={`${styles.navButton} ${styles.prevButton}`}
          onClick={goToPreviousMonth}
          disabled={disabled}
          aria-label="Previous month"
        >
          <svg className={styles.navIcon} viewBox="0 0 24 24">
            <path d="M15.41 7.41L14 6l-6 6 6 6 1.41-1.41L10.83 12z"/>
          </svg>
        </button>

        <div className={styles.monthYearContainer}>
          <button
            type="button"
            className={styles.monthYearButton}
            onClick={() => {
              setShowDropdown(!showDropdown);
              setSelectedYear(null); // Reset when opening fresh
            }}
            disabled={disabled}
            aria-label="Select year"
            aria-expanded={showDropdown}
          >
            <span className={styles.monthYear}>
              {formatMonthYear(currentMonth)}
            </span>
            <svg className={styles.dropdownIcon} viewBox="0 0 24 24">
              <path d="M7 10l5 5 5-5z"/>
            </svg>
          </button>

          {showDropdown && (
            <div className={styles.yearDropdown}>
              {!selectedYear ? (
                /* Show year selection first */
                <div className={styles.yearList}>
                  {yearOptions.map((year: number) => (
                    <button
                      key={year}
                      type="button"
                      className={`${styles.yearButton} ${
                        currentMonth.getFullYear() === year ? styles.selected : ''
                      }`}
                      onClick={() => handleYearSelect(year)}
                      disabled={disabled}
                    >
                      {year}
                    </button>
                  ))}
                </div>
              ) : (
                /* Show month selection after year is selected */
                <div className={styles.monthGrid}>
                  {MONTHS.map((month, index) => (
                    <button
                      key={month}
                      type="button"
                      className={`${styles.monthButton} ${
                        currentMonth.getMonth() === index ? styles.selected : ''
                      }`}
                      onClick={() => handleMonthSelect(index)}
                      disabled={disabled}
                    >
                      {month.slice(0, 3)} {/* Show first 3 letters */}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        <button
          type="button"
          className={`${styles.navButton} ${styles.nextButton}`}
          onClick={goToNextMonth}
          disabled={disabled}
          aria-label="Next month"
        >
          <svg className={styles.navIcon} viewBox="0 0 24 24">
            <path d="M8.59 16.59L10 18l6-6-6-6-1.41 1.41L13.17 12z"/>
          </svg>
        </button>
      </div>

      {/* Calendar Grid */}
      <div className={styles.grid}>
        {/* Weekday Headers */}
        <div className={styles.weekdays}>
          {showWeekNumbers && <div className={styles.weekNumber}>Wk</div>}
          {WEEKDAYS.map((weekday, index) => (
            <div key={weekday} className={styles.weekday}>
              {formatWeekday(new Date(2023, 0, index + 1))}
            </div>
          ))}
        </div>

        {/* Calendar Days */}
        <div className={styles.days}>
          {calendarDays.map((date, index) => {
            const dateKey = `${date.getFullYear()}-${date.getMonth()}-${date.getDate()}`;
            const selected = isSelected(date);
            const today = isToday(date);
            const currentMonth = isCurrentMonth(date);
            const dateDisabled = isDisabled(date);

            // Calculate week number for the first day of each week
            const isFirstDayOfWeek = index % 7 === 0;
            const weekNumber = isFirstDayOfWeek && showWeekNumbers
              ? Math.ceil((date.getDate() - date.getDay() + 1) / 7)
              : null;

            return (
              <div key={dateKey} className={styles.dayWrapper}>
                {showWeekNumbers && isFirstDayOfWeek && (
                  <div className={styles.weekNumber}>
                    {weekNumber}
                  </div>
                )}

                <button
                  type="button"
                  className={`
                    ${styles.day}
                    ${selected ? styles.selected : ''}
                    ${today ? styles.today : ''}
                    ${!currentMonth ? styles.otherMonth : ''}
                    ${dateDisabled ? styles.disabled : ''}
                  `}
                  onClick={() => handleDateSelect(date)}
                  onKeyDown={(e) => handleKeyDown(e, date)}
                  disabled={dateDisabled}
                  aria-selected={selected}
                  aria-label={`${formatDay(date)} ${formatMonthYear(date)} ${date.getFullYear()}${selected ? ' (selected)' : ''}`}
                  tabIndex={currentMonth && !dateDisabled ? 0 : -1}
                >
                  {formatDay(date)}
                </button>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

export default Calendar;
